/**
 * landingResolver — DEV-1413 (slice 2), the runtime landing walk.
 *
 * SCOPING_COMMUNITY_MEMBERSHIP.md §8.4 ruling 1: the membership row stores
 * the DECLARED community even when that community is inactive (dormant
 * membership). Every "where does this user land" read resolves AT RUNTIME:
 *
 *   declared community, if `is_active`
 *   → else walk the parent chain to the nearest `is_active` ancestor
 *     (Boston → Massachusetts → United States)
 *   → else the top active root, resolved DYNAMICALLY from the tree
 *     (never a hardcoded country — §8.11 ruling 5).
 *
 * The resolved ancestor is NEVER written back as the membership — this
 * module is read-only and the ONLY sanctioned landing-resolution logic on
 * the frontend. §8.11 ruling 3: the landing walk is a frontend contract in
 * slice 2; server-side landing reads are out of scope and must not
 * re-implement this ad hoc.
 *
 * Consumers: App.tsx (LeftNav Communities-click landing target),
 * CommunitySwitcherRail (member-lens dormant-membership display,
 * DEV-1415 ruling condition 1).
 *
 * GUID comparisons are case-insensitive per CLAUDE.md.
 */

export interface LandingEntityLike {
  entity_guid: string;
  name?: string;
  entity_type?: string | null;
  is_active?: boolean;
  /** Workshop marker — personal workshops are never a landing target. */
  personal_owner_user_guid?: string | null;
}

export interface LandingRelationshipLike {
  parent_entity_guid?: string;
  child_entity_guid?: string;
  relationship_type?: string;
}

export type LandingResolver = (
  declaredGuid: string | null | undefined,
) => string | null;

/** Defensive bound against cycles in malformed relationship data
 *  (mirrors walkAncestorChain in api/communityMetadata.ts). */
const MAX_WALK = 16;

function isActiveEntity(e: LandingEntityLike | undefined): boolean {
  return !!e && e.is_active !== false && !e.personal_owner_user_guid;
}

function isGeoType(e: LandingEntityLike | undefined): boolean {
  return String(e?.entity_type ?? '').toLowerCase().startsWith('geo_');
}

/**
 * Build a resolver over the loaded entities/relationships. Callers should
 * memoize the resolver on [entities, relationships] and invoke it per
 * declared GUID — map construction happens once per data change.
 *
 * Parent edges mirror the DEV-947 chain filter (relationship_type
 * parent_of/contains) so the walk follows the same edges as the app's
 * other ancestor helpers.
 */
export function createLandingResolver(
  entities: LandingEntityLike[] | undefined,
  relationships: LandingRelationshipLike[] | undefined,
): LandingResolver {
  const entityByGuid = new Map<string, LandingEntityLike>();
  for (const e of entities || []) {
    const g = String(e?.entity_guid || '').toUpperCase();
    if (g) entityByGuid.set(g, e);
  }

  const parentByChild = new Map<string, string>();
  for (const r of relationships || []) {
    const rt = String(r?.relationship_type ?? 'parent_of').toLowerCase();
    if (rt !== 'parent_of' && rt !== 'contains') continue;
    const child = String(r?.child_entity_guid || '').toUpperCase();
    const parent = String(r?.parent_entity_guid || '').toUpperCase();
    if (child && parent && !parentByChild.has(child)) {
      parentByChild.set(child, parent);
    }
  }

  /** Depth of an entity in the tree (root = 0), cycle-safe. */
  const depthOf = (guidUpper: string): number => {
    let depth = 0;
    let cur = parentByChild.get(guidUpper);
    const seen = new Set<string>([guidUpper]);
    while (cur && !seen.has(cur) && depth < MAX_WALK) {
      seen.add(cur);
      depth++;
      cur = parentByChild.get(cur);
    }
    return depth;
  };

  // Terminal fallback: the top ACTIVE root, resolved dynamically — the
  // active GEO community with the smallest tree depth (name as the
  // deterministic tie-break). Geo-only: landing is a place, and org
  // communities are never a geography-walk target. Computed lazily and
  // cached for the resolver's lifetime.
  let cachedFallback: string | null | undefined;
  const topActiveRoot = (): string | null => {
    if (cachedFallback !== undefined) return cachedFallback;
    let best: { guid: string; depth: number; name: string } | null = null;
    for (const [gUpper, e] of entityByGuid) {
      if (!isActiveEntity(e) || !isGeoType(e)) continue;
      const d = depthOf(gUpper);
      const n = String(e.name || '').toLowerCase();
      if (
        best === null ||
        d < best.depth ||
        (d === best.depth && n < best.name)
      ) {
        best = { guid: e.entity_guid, depth: d, name: n };
      }
    }
    cachedFallback = best ? best.guid : null;
    return cachedFallback;
  };

  return (declaredGuid: string | null | undefined): string | null => {
    const start = String(declaredGuid || '').trim();
    if (!start) return null; // no declaration → caller's own fallback chain

    // Declared → nearest active ancestor. The declared row itself is
    // returned when active (stored casing preserved via the entity map).
    let cur: string | undefined = start.toUpperCase();
    const seen = new Set<string>();
    let hops = 0;
    while (cur && !seen.has(cur) && hops <= MAX_WALK) {
      seen.add(cur);
      const entity = entityByGuid.get(cur);
      if (isActiveEntity(entity)) return entity!.entity_guid;
      cur = parentByChild.get(cur);
      hops++;
    }

    // Declared exists but nothing in its chain is active → top active root.
    return topActiveRoot();
  };
}

/**
 * One-shot convenience for callers resolving a single GUID outside a memo.
 * Prefer createLandingResolver + caller-side memoization in components.
 */
export function resolveLandingCommunityGuid(
  declaredGuid: string | null | undefined,
  entities: LandingEntityLike[] | undefined,
  relationships: LandingRelationshipLike[] | undefined,
): string | null {
  return createLandingResolver(entities, relationships)(declaredGuid);
}
